import {HttpClient, HttpParams} from '@angular/common/http';
import {Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { Observable, Subject, Subscriber } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { DisplayListRow } from '../../interfaces/display-list-row.interface';
import { FilterOutput } from '../../interfaces/filter-outpt.interface';
import { FilterQueryValue } from '../../interfaces/filter-query-value.inteface';
import { FilterSet } from '../../interfaces/filter-set.interface';
import { ListConfig } from '../../interfaces/list-config.interface';
import { ContentPage } from '../../page.interface';
import { DataService } from '../../services/data/data.service';
import { FiltersService } from '../../services/filters/filters.service';
import { SimpleFiltersService } from '../../services/simple-filters/simple-filters.service';
import { Utils } from '../../utils/utils.class';
import { ProcessAction } from '../../interfaces/process-action.interface';
import { ProcessEntry } from '../../interfaces/screen-actions.interface';
import { isString } from 'util';

@Component({
  selector: 'sellions-layout-renderer-list',
  templateUrl: './list.component.html',
})
export class ListRendererComponent implements OnInit, OnDestroy {
  @ViewChild('itemList') itemList: ElementRef;
  @ViewChild(MatPaginator) paginator: MatPaginator;

  @Input() config: ListConfig;
  @Input() database: any;
  @Input() noDataText: string;
  @Input() sortByKey: string;
  @Input() sortByDirection: 'ASC' | 'DESC';
  @Input() baseUrl: string;
  @Input() emptyListMessage: string;
  @Input() dataProccessingFunction: (data: any) => any = (data: any) => data;
  @Input() urlParams: { [key: string]: string };
  @Input() paginationEnabled: boolean = false;
  @Input() page: number = 0;
  @Input() size: number = 5;
  @Input() sizes: number[] = [5, 10, 15];
  @Input() params: any;

  @Output() navigationEvent = new EventEmitter<{
    viewId: string;
    element?: Object;
    objectKey?: Object;
    viewComponent?: string;
  }>();
  @Output() loadingEvent = new EventEmitter<boolean>();
  @Output() elementClicked = new EventEmitter<{
    element: Object;
    target: EventTarget;
  }>();
  @Output() receivedData = new EventEmitter<any[]>();
  @Output() contextMenuAction = new EventEmitter<ProcessAction>();

  public loading: boolean;
  private unsubscribe$ = new Subject<void>();
  public data: any[] = [];
  public totalElements: number = 0;

  constructor(
    private dataService: DataService,
    private filtersService: FiltersService,
    private simpleFiltersService: SimpleFiltersService,
    public elementReference: ElementRef,
    private httpClient: HttpClient,
  ) {}

  ngOnInit() {
    this.filtersService.filterSetSubject$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((filterSet: FilterSet) => {
          let searchQuery = filterSet && filterSet.searchQuery || '';
          if (this.paginationEnabled &&  this.paginator) {
            this.paginator.page
              .pipe(takeUntil(this.unsubscribe$))
              .subscribe((page) => {
                this.page = page.pageIndex;
                this.size = page.pageSize;
                this.getData(searchQuery, filterSet && filterSet.filters, filterSet && filterSet.filterFunction);
              });
          } else {
            if (filterSet && filterSet.name === this.config.name) {
              this.getData(searchQuery, filterSet && filterSet.filters, filterSet && filterSet.filterFunction);
            } else {
              this.getData(searchQuery);
            }
          }
        },
        (error) => console.error(error),
      );

    this.simpleFiltersService
      .getFilter(this.config.name)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((filters: { [key: string]: string }) => {
        const isEmpty = Object.keys(filters).length === 0;
        const filterSet = this.filtersService.filterSetSubject$.value;
        if (isEmpty) return;
        this.getOnlineData(this.getDataUrl(), filterSet && filterSet.searchQuery || '', undefined, undefined, filters);
      });
  }

  public reload() {
    const filterSet = this.filtersService.filterSetSubject$.value;
    this.getData(filterSet && filterSet.searchQuery, filterSet && filterSet.filters, filterSet && filterSet.filterFunction);
  }
  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  getData(searchQuery: string, filters?: FilterOutput[], filterFunction?: (o: object) => boolean) {
    this.data = [];

    const dataSourceFilters = this.getDataSourceFilterOutputs();
    filters = this.combineFilters(dataSourceFilters, filters);

    if ((this.config && this.config.dataSourceConfig.operationType === 'OFFLINE') || !this.baseUrl) {
      this.getOfflineData(searchQuery, filters);
    } else {
      const url = this.getDataUrl(filters, searchQuery);
      this.getOnlineData(url, searchQuery, filters, filterFunction);
    }
  }

  private getDataSourceFilterOutputs(): FilterOutput[] {
    const filters = this.config.dataSourceConfig.filters || [];

    return filters.map(filter => {
      const queryFilter = {...filter};
      queryFilter.value = Utils.evaluateVariablesString(filter.value, this.params);
      return {
        type: filter.type,
        value: filter.value,
        column: filter.column,
        queryValue: queryFilter
      };
    });
  }

  public getParams(){
    return this.params;
  }

  private combineFilters(dataSourceFilters: FilterOutput[], filters?: FilterOutput[]): FilterOutput[] {
    if (!filters) {
      filters = [];
    }
    return dataSourceFilters.concat(filters);
  }

  private getDataUrl(filters?: FilterOutput[], searchQuery?: string): string {
    let url = this.dataService.getUrl(this.baseUrl, this.config.dataSourceConfig.onlineDataSourceUrl);
    url = Utils.evaluateVariablesString(url, this.params)
    url = this.addParamsToUrl(url, filters, searchQuery);

    return url;
  }

  private getParamsAsString(): string {
    let str = '';
    Object.keys(this.urlParams).forEach((param) => (str += param + '=' + this.urlParams[param] + '&'));
    return str.slice(0, -1);
  }

  private addParamsToUrl(url: string, filters?: FilterOutput[], searchQuery?: string): string {
    if (filters) {
      url += this.generateFilterUrl(filters);
    }

    if (searchQuery) {
      url += this.generateSearchUrl(url, searchQuery);
    }

    //TODO ujednolicić budowanie urla dla listy i tabeli, ujednolicić dostepne data source'y
    if (this.config.dataSourceConfig.sortProperty) {
      const sortDirection = this.config.dataSourceConfig.sortDirection || 'ASC';
      url = this.appendSeparator(url) + 'sort=' + this.config.dataSourceConfig.sortProperty + ',' + sortDirection;
    }

    if (this.urlParams) {
      url = this.appendSeparator(url) + this.getParamsAsString()
    }

    return url;
  }

  private appendSeparator(url: string): string {
    const separator = url.indexOf('?') === -1 ? '?' : '&';
    return url + separator;
  }

  private generateFilterUrl(filters?: FilterOutput[]): string {
    const queryFilters: FilterQueryValue[] = [];
    filters.forEach((filter) => {
      if (filter.queryValue.value) {
        queryFilters.push(filter.queryValue);
      }
    });

    if (queryFilters.length < 1) {
      return '';
    }

    return '?filters=' + encodeURIComponent(JSON.stringify({ filters: queryFilters }));
  }

  private generateSearchUrl(url: string, searchQuery: string): string {
    if(url.includes('?')) {
      return '&searchQuery=' + searchQuery;
    }

    return '?searchQuery=' + searchQuery;
  }

  private appendFilters(params: HttpParams, filters: { [key: string]: string }): HttpParams {
    Object.keys(filters).forEach((key) => (params = params.append(key, filters[key])));
    return params;
  }

  private getOnlineData(
    url: string,
    searchQuery: string,
    filters?: FilterOutput[],
    filterFunction?: (o: object) => boolean,
    simpleFilters?: { [key: string]: string },
  ) {
    this.changeLoadingStatus(true);

    let params: HttpParams = new HttpParams();
    if (this.paginationEnabled) {
      params = params
        .append('page', this.page.toString())
        .append('size', this.size.toString());
    }

    if (simpleFilters) {
       params = this.appendFilters(params, simpleFilters)
    }

    if (filters) {
      filters.forEach(f => {
        if(f.value && isString(f.value)){
          let evaluatedValue = Utils.evaluateVariablesString(f.value, this.params);
          if(evaluatedValue){
            params = params.append(f.column, evaluatedValue);
          }
        }
      })
    }

    this.httpClient
      .get(url, { params })
      .pipe(
        takeUntil(this.unsubscribe$),
        finalize(() => this.changeLoadingStatus(false)),
      )
      .subscribe(
        (data: ContentPage) => {
          this.data = this.processData(data.content, searchQuery, null, filterFunction);
          this.dataService
            .saveData(
              this.data,
              (_value, index) => this.config.dataSourceConfig.offlineStorageKey + Utils.pad(index, this.dataService.getIndexLength()),
            )
            .catch((error) => console.error(error));
          this.receivedData.emit(this.data);

          this.totalElements = data.totalElements;
          this.changeLoadingStatus(false);
        },
        (error) => {
          if (this.config.dataSourceConfig.operationType === 'MIXED' && error.status === 0) {
            this.getOfflineData(searchQuery, filters);
          }
          console.error(error);
        },
      );
  }

  private getOfflineData(searchQuery: string, filters?: FilterOutput[], filterFunction?: (o: object) => boolean) {
    this.changeLoadingStatus(true);

    new Observable((observer: Subscriber<any[]>) => this.dataService.emitData(observer, this.config.dataSourceConfig.offlineStorageKey))
      .pipe(
        takeUntil(this.unsubscribe$),
        finalize(() => this.changeLoadingStatus(false)),
      )
      .subscribe(
        (elements: Object[]) => {
          this.data = this.data.concat(this.processData(elements, searchQuery, filters, filterFunction));
          this.receivedData.emit(this.data);
        },
        (error) => console.error(error),
      );
  }

  private processData(elements: Object[], searchQuery?: string, filters?: FilterOutput[], filterFunction?: (o: object) => boolean): Object[] {
    const unsorted = elements
      .map((element) => this.evaluateDisplayStrings(this.dataProccessingFunction ? this.dataProccessingFunction(element) : element) as any)
      .filter(
        (element) =>
          this.checkAgainstQuery(element, searchQuery) &&
          this.checkAgainstFilters(element, filters) &&
          this.checkAgainstFilterFunction(element, filterFunction),
      );

    if (this.sortByKey && this.sortByDirection) {
      return unsorted.sort((a, b) => this.sort(a, b, this.sortByKey, this.sortByDirection));
    } else {
      return unsorted;
    }
  }

  private checkAgainstQuery(element: DisplayListRow, query: string): boolean {
    if (query) {
      const minimalElement = {
        title: element.title,
        subtitle: element.subtitle,
        content: element.content,
      };

      return Utils.checkAgainstQuery(minimalElement, query);
    } else {
      return true;
    }
  }

  private checkAgainstFilters(element: Object, filters: any[]): boolean {
    if (filters) {
      let result = true;
      filters.forEach((filter) => {
        switch (filter.type) {
          case 'DICTIONARY':
            if (!Utils.checkAgainstDictionaryEntry(filter.value, element[filter.name])) result = false;
            break;
          case 'DATE':
            if (!Utils.checkAgainstDate(element[filter.name], filter.value)) result = false;
            break;
          default:
            if (!Utils.checkForStringMatch(element[filter.name], filter.value)) result = false;
            break;
        }
      });

      return result;
    } else {
      return true;
    }
  }

  private sort(elem1: any, elem2: any, sortByKey: string, sortByDirection: 'ASC' | 'DESC'): number {
    if (!sortByKey || !sortByDirection) {
      return -1;
    }

    let a = elem1[sortByKey];
    let b = elem2[sortByKey];

    if (!a || !b) {
      return -1;
    }

    let result = -1;

    if (a.name) {
      a = a.name;
      b = b.name;
    }

    if (a > b) {
      result = -1;
    } else {
      result = 1;
    }

    if (sortByDirection === 'DESC') {
      return result;
    } else {
      return -result;
    }
  }

  private checkAgainstFilterFunction(element: object, filterFunction?: (o: object) => boolean) {
    return filterFunction ? filterFunction(element) : true;
  }

  private evaluateDisplayStrings(object: DisplayListRow): Object {
    object.title = Utils.evaluateString(this.config.displayConfig.rowLayoutMappings.title, object);
    object.subtitle = Utils.evaluateString(this.config.displayConfig.rowLayoutMappings.subtitle, object);
    object.content = Utils.evaluateString(this.config.displayConfig.rowLayoutMappings.content, object);
    if (this.config.displayConfig.rowLayoutMappings.img) {
      object.img = Utils.evaluateString(this.config.displayConfig.rowLayoutMappings.img, object);
    }

    return object;
  }

  public onClick(element: any, $event: MouseEvent) {
    if (!this.config.onClick) {
      return;
    }

    if (this.config.onClick.behaviour === 'CHANGE_VIEW') {
      this.navigationEvent.emit({
        viewId: this.config.onClick.viewId,
        element,
        viewComponent: this.config.onClick.viewComponent,
      });
      return;
    }

    if (this.config.onClick.behaviour === 'TRIGGER_ELEMENT_CLICK') {
      this.elementClicked.emit({ element, target: $event.target });
      return;
    }

    console.log('Not implemented yet');
  }

  public onContextMenuClicked(element: any, process: ProcessEntry) {
    this.contextMenuAction.emit({ process: process, element: element });
  }

  private changeLoadingStatus(status: boolean): void {
    this.loadingEvent.emit(status);
    this.loading = status;
  }

  isContextMenuVisible(): boolean {
    return this.config && this.config.screenActions && this.config.screenActions.processList && this.config.screenActions.processList.find(process => process.contextMenuVisible) != null;
  }
}



// WEBPACK FOOTER //
// ./src/submodules/sellions_layout_renderer/projects/sellions-layout-renderer-lib/src/lib/components/list/list.component.ts